
import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';

type ProtectedRouteProps = {
  children: ReactNode;
  requiredRoles?: string[];
};

export const ProtectedRoute = ({ children, requiredRoles = [] }: ProtectedRouteProps) => {
  const { 
    user, 
    isLoading, 
    userRoles, 
    canAccessAdminFeatures, 
    canAccessMentorFeatures,
    canAccessAgenticFeatures 
  } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    if (requiredRoles.length > 0) {
      console.log("Protected route role check:", {
        requiredRoles,
        userRoles,
        canAccessAdmin: canAccessAdminFeatures(),
        canAccessMentor: canAccessMentorFeatures(),
        canAccessAgentic: canAccessAgenticFeatures()
      });
    }
  }, [requiredRoles, userRoles, canAccessAdminFeatures, canAccessMentorFeatures, canAccessAgenticFeatures]);
  
  // Show loading indicator while checking auth state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // If not logged in, redirect to sign in page
  if (!user) {
    // Store the current path to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to="/auth/signin" replace state={{ from: location }} />;
  }
  
  // If roles are required, check user roles
  if (requiredRoles.length > 0) {
    let hasRequiredRole = false;
    
    // Check if user has any of the required roles
    for (const role of requiredRoles) {
      if (
        (role === 'admin' && canAccessAdminFeatures()) || 
        (role === 'mentor' && canAccessMentorFeatures()) ||
        (role === 'agentic' && canAccessAgenticFeatures()) ||
        (role === 'student') // Everyone can access student features
      ) {
        hasRequiredRole = true;
        break;
      }
    }
    
    // If user doesn't have required role, redirect to dashboard
    if (!hasRequiredRole) {
      console.warn(`User lacks required role(s): ${requiredRoles.join(', ')}. User roles: ${userRoles.join(', ')}`);
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return <>{children}</>;
};

export default ProtectedRoute;
